import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, Award, TrendingUp, Users, DollarSign, Calendar, Target, Package, Tag, ShoppingCart, Shield, Layers, AlertCircle } from "lucide-react";
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseURL } from '../../../../Services/api/baseURL';
import moment from 'moment';

const getBoostDisplay = (campaign) => {
  if (campaign.boostType === 'multiplier') {
    return `${campaign?.boostValue?.multiplier}x Points`;
  } else if (campaign?.boostType === 'fixed_extra') {
    return `+${campaign?.boostValue?.fixedPoints} Points`;
  } else {
    return `${campaign?.boostValue?.replacementRate} pts/$`;
  }
};

const CampaignDetailsPage = () => {
    const { campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);

    const  [page,setPage] = useState(1)
    const pageSize = 10

    const getToken = () => JSON.parse(localStorage.getItem("token"));

    const fetchBoostData = async () => {
        try {
            setLoading(true);
            const token = getToken();
            const response = await axios.get(
                `${baseURL}/api/admin/get-boost-by-id/${campaignId}`,
                { headers: { Authorization: `${token}` } }
            );
            setCampaign(response.data.data || response.data);
        } catch (error) {
            console.error(error);
            toast.error("Error fetching campaign details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBoostData();
    }, [campaignId]);

    const paginatedUsage = campaign?.usageHistory.slice(
        (page-1)*pageSize,
        page*pageSize
    )

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><div className="text-gray-600">Loading...</div></div>;
    }

    if (!campaign) {
        return <div className="flex items-center justify-center h-screen text-red-600">Campaign not found</div>;
    }

    const daysMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const activeDays = campaign.schedule.daysOfWeek?.map(d => daysMap[d]).join(', ') || 'All Days';

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                            <button onClick={() => window.history.back()} className="mt-1 p-1 hover:bg-gray-100 rounded">
                                <ChevronLeft size={20} className="text-gray-600" />
                            </button>
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                    <h1 className="text-xl font-bold text-gray-900">{campaign.name}</h1>
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                        {campaign.status}
                                    </span>
                                    {campaign.badgeText && (
                                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                                            {campaign.badgeText}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600">{campaign.description}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Analytics Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Award className="text-blue-600" size={18} />
                            <span className="text-xs text-gray-600 font-medium">Boost Value</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{getBoostDisplay(campaign)}</p>
                        <p className="text-xs text-gray-500 mt-1 capitalize">{campaign.boostType.replace('_', ' ')}</p>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <TrendingUp className="text-green-600" size={18} />
                            <span className="text-xs text-gray-600 font-medium">Points Awarded</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{campaign.analytics.totalPointsAwarded.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">{campaign.analytics.totalOrders} orders</p>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="text-emerald-600" size={18} />
                            <span className="text-xs text-gray-600 font-medium">Revenue</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">${campaign.analytics.revenue.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">Generated</p>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Users className="text-indigo-600" size={18} />
                            <span className="text-xs text-gray-600 font-medium">Customers</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{campaign.analytics.uniqueCustomers.length}</p>
                        <p className="text-xs text-gray-500 mt-1">Unique users</p>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-3 gap-4 mb-4">
                    {/* Schedule */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Calendar className="text-gray-700" size={18} />
                            <h3 className="font-semibold text-gray-900">Schedule</h3>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Start Date:</span>
                                <span className="font-medium">{moment(campaign.schedule.startDate).format("DD/MM/YYYY")}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">End Date:</span>
                                <span className="font-medium">{moment(campaign.schedule.endDate).format("DD/MM/YYYY")}</span>
                            </div>
                            {campaign.schedule.timeRestrictions.enabled && (
                                <>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Time:</span>
                                        <span className="font-medium">{campaign.schedule.timeRestrictions.startTime} - {campaign.schedule.timeRestrictions.endTime}</span>
                                    </div>
                                </>
                            )}
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Days:</span>
                                        <span className="font-medium">{activeDays}</span>
                                    </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Timezone:</span>
                                <span className="font-medium">{campaign.schedule.timezone}</span>
                            </div>
                        </div>
                    </div>

                    {/* Caps & Limits */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Shield className="text-gray-700" size={18} />
                            <h3 className="font-semibold text-gray-900">Caps & Limits</h3>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Per Order:</span>
                                <span className="font-medium">{campaign.caps.maxPointsPerOrder || 'Unlimited'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Per Customer:</span>
                                <span className="font-medium">{campaign.caps.maxPointsPerCustomer || 'Unlimited'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Uses/Customer:</span>
                                <span className="font-medium">{campaign.caps.maxUsesPerCustomer || 'Unlimited'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Uses:</span>
                                <span className="font-medium">{campaign.caps.currentTotalUses}{campaign.caps.maxTotalUses ? ` / ${campaign.caps.maxTotalUses}` : ''}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Reset Period:</span>
                                <span className="font-medium capitalize">{campaign.caps.resetPeriod}</span>
                            </div>
                        </div>
                    </div>

                    {/* Stacking Rules */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Layers className="text-gray-700" size={18} />
                            <h3 className="font-semibold text-gray-900">Stacking Rules</h3>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Allow Stacking:</span>
                                <span className={`font-medium ${campaign.stacking.allowStacking ? 'text-green-600' : 'text-red-600'}`}>
                                    {campaign.stacking.allowStacking ? 'Yes' : 'No'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Priority:</span>
                                <span className="font-medium">{campaign.stacking.priority}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Mode:</span>
                                <span className="font-medium capitalize">{campaign.stacking.stackingMode}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Target Conditions */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Target size={18} />
                        Target Conditions
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        {/* Products */}
                        {campaign.targetConditions.products.enabled && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Package size={16} className="text-orange-600" />
                                    <h4 className="font-medium text-sm">Products ({campaign.targetConditions.products.productIds.length})</h4>
                                </div>
                                <div className="space-y-1">
                                    {campaign.targetConditions.products.productIds.slice(0, 3).map((product) => (
                                        <div key={product._id} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                            {product.productName}
                                        </div>
                                    ))}
                                    {campaign.targetConditions.products.productIds.length > 3 && (
                                        <div className="text-xs text-gray-500 italic">
                                            +{campaign.targetConditions.products.productIds.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Variants */}
                        {campaign.targetConditions.products.enabled && campaign.targetConditions.products.variantIds?.length > 0 && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Package size={16} className="text-orange-600" />
                                    <h4 className="font-medium text-sm">Variants ({campaign.targetConditions.products.variantIds.length})</h4>
                                </div>
                                <div className="space-y-1">
                                    {campaign.targetConditions.products.variantIds.slice(0, 3).map((variant) => (
                                        <div key={variant._id} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                            {variant.productName ? `${variant.productName} — ${variant.variantName}` : variant.variantName}
                                            {variant.vol && ` (${variant.vol})`}
                                        </div>
                                    ))}
                                    {campaign.targetConditions.products.variantIds.length > 3 && (
                                        <div className="text-xs text-gray-500 italic">
                                            +{campaign.targetConditions.products.variantIds.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Categories */}
                        {campaign.targetConditions.categories.enabled && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Layers size={16} className="text-purple-600" />
                                    <h4 className="font-medium text-sm">Categories ({campaign.targetConditions.categories.categoryIds.length})</h4>
                                </div>
                                <div className="space-y-1">
                                    {campaign.targetConditions.categories.categoryIds.map((category) => (
                                        <div key={category._id} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                            {category.collection_name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Channels */}
                        {campaign.targetConditions.channels.enabled && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShoppingCart size={16} className="text-blue-600" />
                                    <h4 className="font-medium text-sm">Channels</h4>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {campaign.targetConditions.channels.channelList.map((channel) => (
                                        <span key={channel} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full capitalize">
                                            {channel}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Customer Tags */}
                        {campaign.targetConditions.customerTags.enabled && campaign.targetConditions.customerTags.tagIds.length > 0 && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Tag size={16} className="text-pink-600" />
                                    <h4 className="font-medium text-sm">Customer Tags ({campaign.targetConditions.customerTags.tagIds.length})</h4>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {campaign.targetConditions.customerTags.tagIds.map((tag) => (
                                        <span key={tag._id} className="text-xs bg-pink-50 text-pink-700 px-2 py-1 rounded-full">
                                            {tag.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Order Criteria */}
                        {campaign.targetConditions.orderCriteria.enabled && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <DollarSign size={16} className="text-green-600" />
                                    <h4 className="font-medium text-sm">Order Criteria</h4>
                                </div>
                                <div className="space-y-1 text-xs">
                                    {campaign.targetConditions.orderCriteria.minOrderValue && (
                                        <div className="text-gray-600">
                                            Min: <span className="font-medium">${campaign.targetConditions.orderCriteria.minOrderValue}</span>
                                        </div>
                                    )}
                                    {campaign.targetConditions.orderCriteria.maxOrderValue && (
                                        <div className="text-gray-600">
                                            Max: <span className="font-medium">${campaign.targetConditions.orderCriteria.maxOrderValue}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Usage History */}
                <div className="bg-white rounded-lg shadow-sm p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">
                        Usage History ({campaign.usageHistory?.length || 0})
                    </h3>
                    {campaign.usageHistory && campaign.usageHistory.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="text-left py-2 px-3 font-medium text-gray-700">Order ID</th>
                                        <th className="text-left py-2 px-3 font-medium text-gray-700">Customer</th>
                                        <th className="text-left py-2 px-3 font-medium text-gray-700">Channel</th>
                                        <th className="text-right py-2 px-3 font-medium text-gray-700">Order Value</th>
                                        <th className="text-right py-2 px-3 font-medium text-gray-700">Base Points</th>
                                        <th className="text-right py-2 px-3 font-medium text-gray-700">Points Awarded</th>
                                        <th className="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {paginatedUsage.map((usage, index) => (
                                        <tr key={usage._id || index} className="hover:bg-gray-50">
                                            <td className="py-2 px-3 text-gray-900 font-mono text-xs">
                                                {usage.orderId?.sequence_number || usage.orderId}
                                            </td>
                                            <td className="py-2 px-3 text-gray-600">
                                                {typeof usage.userId === 'string' && usage.userId.includes('@') 
                                                    ? usage.userId 
                                                    : usage.userId?.email || 'N/A'}
                                            </td>
                                            <td className="py-2 px-3">
                                                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded capitalize">
                                                    {usage.channel}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 text-right font-medium text-gray-900">
                                                ${usage.orderValue}
                                            </td>
                                            <td className="py-2 px-3 text-right text-gray-600">
                                                {usage.basePoints}
                                            </td>
                                            <td className="py-2 px-3 text-right font-semibold text-green-600">
                                                +{usage.pointsAwarded}
                                            </td>
                                            <td className="py-2 px-3 text-gray-600">
                                                {moment(usage.date).format("DD/MM/YY HH:mm")}
                                                {/* {new Date(usage.date).toLocaleDateString()} {new Date(usage.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} */}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                                <div className='flex justify-between items-center mt-3'>
                                <button disabled={page===1}
                                onClick={()=>setPage(page-1)}
                                className='px-3 py-1 bg-gray-100 rounded disable:opacity-50'
                                >prev</button>
                                <span className="text-sm text-gray-600">Page {page} of {Math.ceil(campaign.usageHistory.length/pageSize)}</span>
                                <button
                                onClick={()=>setPage(page+1)}
                                disabled={page>= Math.ceil(campaign.usageHistory.length/pageSize)}
                                className='px-3 py-1 bg-gray-100 rounded disable:opacity-50'
                                >next</button>
                                </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <AlertCircle size={24} className="mx-auto mb-2 text-gray-400" />
                            <p>No usage history recorded yet</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CampaignDetailsPage;